import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ClipboardList, Search, CheckCircle, AlertCircle, FileText, BarChart3, ArrowRight } from 'lucide-react'
import { useCampaniasPendientes } from '../hooks/useSeguimientoReportes'
import { useLaboratorios } from '@/modules/multichannel/hooks/useConfiguracionCampanias'
import { CampaniasPendientesTable } from '../components/CampaniasPendientesTable'
import { ReporteModal } from '@/modules/cronograma/components/ReporteModal'
import { ReporteWhatsAppModal } from '@/modules/cronograma/components/ReporteWhatsAppModal'
import type { CampaniaPendiente } from '../types'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const WHATSAPP_TIPOS = ['Whatsapp', 'Email + Whatsapp']
const EMAIL_TIPOS = ['Email', 'Email + Whatsapp']

export function SeguimientoReportesPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [marcaPrincipalId, setMarcaPrincipalId] = useState<number | undefined>()
  const [activeTab, setActiveTab] = useState('sin_reporte')

  // Modales de reporte
  const [reporteEmailModalOpen, setReporteEmailModalOpen] = useState(false)
  const [reporteWhatsAppModalOpen, setReporteWhatsAppModalOpen] = useState(false)
  const [selectedCampania, setSelectedCampania] = useState<CampaniaPendiente | null>(null)

  const { data: campanias, isLoading } = useCampaniasPendientes(
    search || undefined,
    marcaPrincipalId,
    activeTab
  )
  const { data: laboratorios } = useLaboratorios({ activo: true })

  const stats = useMemo(() => {
    if (!campanias) return { total: 0, conSeguimiento: 0, sinSeguimiento: 0 }
    const hoy = new Date().toISOString().split('T')[0]
    const conSeguimientoHoy = campanias.filter((c) => c.ultimo_seguimiento === hoy).length
    return {
      total: campanias.length,
      conSeguimiento: conSeguimientoHoy,
      sinSeguimiento: campanias.length - conSeguimientoHoy,
    }
  }, [campanias])

  const handleOpenReporteModal = (campania: CampaniaPendiente) => {
    setSelectedCampania(campania)
    const tipo = campania.tipo || ''
    if (EMAIL_TIPOS.includes(tipo)) {
      setReporteEmailModalOpen(true)
    }
    if (WHATSAPP_TIPOS.includes(tipo)) {
      setReporteWhatsAppModalOpen(true)
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-amber-500/10 p-2">
            <ClipboardList className="h-6 w-6 text-amber-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Seguimiento de Reportes</h1>
            <p className="text-sm text-muted-foreground">
              Campanas con reporte pendiente de generacion
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/multichannel/seguimiento')}
          className="gap-2"
        >
          Seguimiento Multicanal
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatsCard title="Total Pendientes" value={stats.total} icon={ClipboardList} />
        <StatsCard title="Con Seguimiento Hoy" value={stats.conSeguimiento} icon={CheckCircle} />
        <StatsCard title="Sin Seguimiento" value={stats.sinSeguimiento} icon={AlertCircle} />
      </div>

      {/* Filters */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar campana..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={marcaPrincipalId ? String(marcaPrincipalId) : 'all'}
              onValueChange={(v) => setMarcaPrincipalId(v === 'all' ? undefined : Number(v))}
            >
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="Laboratorio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los laboratorios</SelectItem>
                {laboratorios?.map((lab) => (
                  <SelectItem key={lab.id} value={String(lab.id)}>
                    {lab.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs + Table */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="sin_reporte" className="gap-2">
            <FileText className="h-4 w-4" />
            Sin Reporte
          </TabsTrigger>
          <TabsTrigger value="en_recopilacion" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            En Recopilacion
          </TabsTrigger>
        </TabsList>
        <TabsContent value={activeTab} className="mt-4">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
            </div>
          ) : (
            <CampaniasPendientesTable
              campanias={campanias || []}
              showReporteButton={activeTab === 'en_recopilacion'}
              onCreateReporte={handleOpenReporteModal}
              onEditReporte={handleOpenReporteModal}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Modal de Reporte Email */}
      {selectedCampania && (
        <ReporteModal
          open={reporteEmailModalOpen}
          onClose={() => {
            setReporteEmailModalOpen(false)
            if (!reporteWhatsAppModalOpen) {
              setSelectedCampania(null)
            }
          }}
          campaniaId={selectedCampania.id}
          nombreCampania={selectedCampania.nombre_campania}
          tieneReporte={selectedCampania.tiene_reporte}
        />
      )}

      {/* Modal de Reporte WhatsApp */}
      {selectedCampania && (
        <ReporteWhatsAppModal
          open={reporteWhatsAppModalOpen}
          onClose={() => {
            setReporteWhatsAppModalOpen(false)
            if (!reporteEmailModalOpen) {
              setSelectedCampania(null)
            }
          }}
          campaniaId={selectedCampania.id}
          nombreCampania={selectedCampania.nombre_campania}
          nombreLaboratorio={selectedCampania.marca_principal_nombre || selectedCampania.marca}
          fechaPublicacion={selectedCampania.fecha_publicacion}
          tieneReporte={selectedCampania.tiene_reporte}
        />
      )}
    </div>
  )
}

function StatsCard({
  title,
  value,
  icon: Icon,
}: {
  title: string
  value: number
  icon: React.ComponentType<{ className?: string }>
}) {
  return (
    <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-amber-500/15 via-amber-500/5 via-50% to-transparent dark:from-amber-800/20 dark:via-amber-900/10 dark:via-50% dark:to-transparent">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="rounded-lg bg-amber-500/10 p-2">
          <Icon className="h-4 w-4 text-amber-500" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  )
}
